// ============================================================
// AdhesiveDashboard.jsx  —  Noble Enterprises
// Sidebar layout with accordion sub-menus, fully responsive.
//
// HOW THIS FILE IS ORGANISED
//   1. Imports          — React, router, auth, icons, tab components
//   2. NAV              — the single source of truth for the menu
//   3. THEME            — every colour in one object
//   4. Small components — SidebarLink, SidebarGroup
//   5. Main component   — layout, state, rendering
//
// TO ADD A NEW SECTION LATER: add one object to NAV.
// You never touch the rendering code.
// ============================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SettingsTab from './tabs/SettingsTab';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, Film, Factory, Boxes, Receipt, Building2, Settings,
  LogOut, Menu, X, ChevronDown, ChevronRight, ArrowLeft
} from 'lucide-react';

// ------------------------------------------------------------
// 1. TAB COMPONENTS
// ------------------------------------------------------------
import DashboardTab    from './tabs/DashboardTab';
import RawMaterialsTab from './tabs/RawMaterialsTab';
import SuppliersTab    from './tabs/SuppliersTab';
import ProductionTab   from './tabs/ProductionTab';
import InventoryTab    from './tabs/InventoryTab';
import SalesTab        from './tabs/SalesTab';

// ------------------------------------------------------------
// 2. NAVIGATION CONFIG
//
//   key        unique id — also what activeKey state holds
//   label      what the user reads
//   icon       lucide component
//   component  what renders on the right when this key is active
//   children   optional array, same shape (one level deep only)
//
// Every parent needs a component, because clicking a parent
// loads its page AND expands its children.
// ------------------------------------------------------------
const NAV = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    component: DashboardTab,
  },
  {
    key: 'raw-materials',
    label: 'Raw materials',
    icon: Film,
    component: RawMaterialsTab,
    children: [
      { key: 'suppliers', label: 'Suppliers', icon: Building2, component: SuppliersTab },
    ],
  },
  {
    key: 'production',
    label: 'Production',
    icon: Factory,
    component: ProductionTab,
  },
  {
    key: 'inventory',
    label: 'Inventory',
    icon: Boxes,
    component: InventoryTab,
  },
  {
    key: 'sales',
    label: 'Sales',
    icon: Receipt,
    component: SalesTab,
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: Settings,
    component: SettingsTab,
  },
];

// ------------------------------------------------------------
// 3. THEME
// Change a colour here and it changes everywhere.
// ------------------------------------------------------------
const T = {
  sidebarBg:     '#1a2332',
  sidebarBorder: '#2a3647',
  brand:         '#f39c12',
  brandDark:     '#e67e22',
  textOnDark:    '#dde4ed',
  textMutedDark: '#7d8da5',
  pageBg:        '#f5f7fa',
  cardBg:        '#ffffff',
  textPrimary:   '#1a2332',
  textSecondary: '#64748b',
  border:        '#e2e8f0',
};

const SIDEBAR_WIDTH = 272;
const MOBILE_BREAKPOINT = 1024;

// ------------------------------------------------------------
// 4a. SidebarLink — one clickable row
//
// depth 0 = parent row, depth 1 = child row (indented, smaller)
// ------------------------------------------------------------
const SidebarLink = ({ item, isActive, isExpanded, hasChildren, depth, onClick }) => {
  const Icon = item.icon;
  const [hover, setHover] = useState(false);

  // Active gets a tinted background, hover gets a faint one
  const background = isActive
    ? 'rgba(243, 156, 18, 0.15)'
    : hover
      ? 'rgba(255, 255, 255, 0.05)'
      : 'transparent';

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        padding: depth === 0 ? '17px 20px' : '13px 20px 13px 56px',
        background,
        border: 'none',
        borderLeft: isActive ? `3px solid ${T.brand}` : '3px solid transparent',
        color: isActive ? '#ffffff' : T.textOnDark,
        fontSize: depth === 0 ? '18px' : '16px',
        fontWeight: isActive ? '600' : '500',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background 0.15s',
        fontFamily: 'inherit',
      }}
    >
      <Icon size={depth === 0 ? 23 : 20} strokeWidth={2} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{item.label}</span>

      {/* Chevron only on parents that have children */}
      {hasChildren && (
        isExpanded
          ? <ChevronDown size={17} style={{ color: T.textMutedDark }} />
          : <ChevronRight size={17} style={{ color: T.textMutedDark }} />
      )}
    </button>
  );
};

// ------------------------------------------------------------
// 4b. SidebarGroup — a parent plus its children
// ------------------------------------------------------------
const SidebarGroup = ({ item, activeKey, expandedKeys, onSelect }) => {
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const isExpanded  = expandedKeys.includes(item.key);

  return (
    <div>
      <SidebarLink
        item={item}
        depth={0}
        isActive={activeKey === item.key}
        isExpanded={isExpanded}
        hasChildren={hasChildren}
        onClick={() => onSelect(item, false)}
      />

      {/* Children render only when the parent is expanded */}
      {hasChildren && isExpanded && item.children.map(child => (
        <SidebarLink
          key={child.key}
          item={child}
          depth={1}
          isActive={activeKey === child.key}
          isExpanded={false}
          hasChildren={false}
          onClick={() => onSelect(child, true)}
        />
      ))}
    </div>
  );
};

// ------------------------------------------------------------
// 5. MAIN COMPONENT
// ------------------------------------------------------------
const AdhesiveDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Which page shows on the right
  const [activeKey, setActiveKey] = useState('dashboard');

  // Which parents are open (array, so several can be open at once)
  const [expandedKeys, setExpandedKeys] = useState([]);

  // Mobile drawer open/closed
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Are we on a narrow screen
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );

  // Watch window size so layout switches between fixed sidebar and drawer
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setDrawerOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ----------------------------------------------------------
  // Clicking a sidebar row
  //
  // Parent → show its page AND toggle its children open
  // Child  → show its page, leave the parent open
  // ----------------------------------------------------------
  const handleSelect = (item, isChild) => {
    setActiveKey(item.key);

    if (!isChild && Array.isArray(item.children) && item.children.length > 0) {
      setExpandedKeys(prev =>
        prev.includes(item.key)
          ? prev.filter(k => k !== item.key)   // open → close
          : [...prev, item.key]                // closed → open
      );
    }

    if (isMobile) setDrawerOpen(false);
  };

  // ----------------------------------------------------------
  // Look up the active item (component + label) in one pass
  // ----------------------------------------------------------
  const findActive = () => {
    for (const item of NAV) {
      if (item.key === activeKey) return item;
      const child = item.children?.find(c => c.key === activeKey);
      if (child) return child;
    }
    return NAV[0];
  };

  const active = findActive();
  const ActiveComponent = active.component;
  const isBusinessman = user?.role === 'businessman';

  // Initials for the avatar circle
  const initials = (user?.full_name || user?.username || 'U')
    .split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  // ----------------------------------------------------------
  // Sidebar markup — shared by desktop and mobile drawer.
  // Only the wrapper positioning differs.
  // ----------------------------------------------------------
  const sidebarContent = (
    <>
      {/* Logo mark only — the full name lives in the top bar */}
      <div style={{
        padding: '18px 18px',
        borderBottom: `1px solid ${T.sidebarBorder}`,
        display: 'flex',
        alignItems: 'center',
      }}>
        <div style={{
          width: '40px', height: '40px', borderRadius: '10px',
          background: `linear-gradient(135deg, ${T.brand} 0%, ${T.brandDark} 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#ffffff', fontWeight: '700', fontSize: '15px', flexShrink: 0,
        }}>
          NE
        </div>

        {/* Close button — mobile drawer only */}
        {isMobile && (
          <button
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
            style={{
              marginLeft: 'auto', background: 'none', border: 'none',
              color: T.textMutedDark, cursor: 'pointer', padding: '4px',
              display: 'flex',
            }}
          >
            <X size={22} />
          </button>
        )}
      </div>

      {/* Menu */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
        {NAV.map(item => (
          <SidebarGroup
            key={item.key}
            item={item}
            activeKey={activeKey}
            expandedKeys={expandedKeys}
            onSelect={handleSelect}
          />
        ))}
      </nav>

      {/* Back to main dashboard */}
      {isBusinessman && (
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${T.sidebarBorder}` }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: '9px',
              padding: '10px 12px', background: 'rgba(255,255,255,0.06)',
              border: `1px solid ${T.sidebarBorder}`, borderRadius: '8px',
              color: T.textOnDark, fontSize: '13px', fontWeight: '500',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <ArrowLeft size={16} />
            All businesses
          </button>
        </div>
      )}
    </>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: T.pageBg,
      fontFamily: "'Inter', sans-serif",
      display: 'flex',
    }}>

      {/* ================= DESKTOP SIDEBAR ================= */}
      {!isMobile && (
        <aside style={{
          width: `${SIDEBAR_WIDTH}px`,
          flexShrink: 0,
          background: T.sidebarBg,
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          height: '100vh',
        }}>
          {sidebarContent}
        </aside>
      )}

      {/* ================= MOBILE DRAWER ================= */}
      {isMobile && drawerOpen && (
        <>
          {/* Backdrop — click anywhere to close */}
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.5)', zIndex: 40,
            }}
          />
          <aside style={{
            position: 'fixed', top: 0, left: 0, bottom: 0,
            width: `${SIDEBAR_WIDTH}px`,
            background: T.sidebarBg,
            display: 'flex', flexDirection: 'column',
            zIndex: 50,
          }}>
            {sidebarContent}
          </aside>
        </>
      )}

      {/* ================= MAIN AREA ================= */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>

        {/* Top bar */}
        <header style={{
          background: T.cardBg,
          borderBottom: `1px solid ${T.border}`,
          padding: isMobile ? '14px 16px' : '20px 28px',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          position: 'sticky',
          top: 0,
          zIndex: 30,
        }}>
          {/* Hamburger — mobile only */}
          {isMobile && (
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: T.textPrimary, padding: '4px', display: 'flex',
              }}
            >
              <Menu size={22} />
            </button>
          )}

          {/* Business name, with the active section as a subtitle */}
          <div style={{ minWidth: 0 }}>
            <h1 style={{
              margin: 0,
              fontSize: isMobile ? '22px' : '32px',
              fontWeight: '700',
              color: T.textPrimary,
              lineHeight: 1.2,
              letterSpacing: '-0.3px',
            }}>
              Noble Enterprises
            </h1>
            <div style={{
              fontSize: isMobile ? '13px' : '15px',
              color: T.textSecondary,
              fontWeight: '500',
              marginTop: '1px',
            }}>
              Adhesive tapes &nbsp;·&nbsp; {active.label}
            </div>
          </div>

          {/* User + logout, pushed right */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
            {!isMobile && (
              <span style={{ fontSize: '13px', color: T.textSecondary, fontWeight: '500' }}>
                {user?.full_name || user?.username || 'User'}
              </span>
            )}
            <div style={{
              width: '32px', height: '32px', borderRadius: '50%',
              background: `linear-gradient(135deg, ${T.brand} 0%, ${T.brandDark} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#ffffff', fontSize: '12px', fontWeight: '700', flexShrink: 0,
            }}>
              {initials}
            </div>
            <button
              onClick={logout}
              aria-label="Logout"
              style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                padding: '8px 13px', background: 'transparent',
                border: `1px solid ${T.border}`, borderRadius: '8px',
                color: T.textSecondary, fontSize: '13px', fontWeight: '500',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <LogOut size={15} />
              {!isMobile && 'Logout'}
            </button>
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, padding: isMobile ? '18px 16px' : '28px' }}>
          <ActiveComponent />
        </main>
      </div>
    </div>
  );
};

export default AdhesiveDashboard;